import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Search, Loader2, ExternalLink, Sparkles, ChevronDown, ChevronUp, CheckCircle2
} from 'lucide-react';
import { components, palette, withOpacity } from '../../../utils/designTokens';
import { isDemoMode, getMockClassifierTestResults } from '../../../services/mockDemoData';
import { v2ServicePath } from '../../../config';
import { useTimeRange } from '../../../contexts/TimeRangeContext';
import { useSessionJwt } from '../../../hooks/useSessionJwt';

const toClassifierKey = (name) =>
  name.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_').replace(/^_+|_+$/g, '').substring(0, 100);

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Semantic first and default — it is the path we want authors on; regex is the
// escape hatch for exact-token cases.
const MATCH_TYPE_OPTIONS = [
  { value: 'semantic', label: 'Semantic' },
  { value: 'keyword', label: 'Regex/Keyword' },
];

// Authoring examples rendered as placeholder guidance inside the definition box.
// See models/docs/classifiers/custom-signals.md § Authoring copy (wizard).
const SIGNAL_EXAMPLES = [
  { about: 'User',     text: 'The user asked to cancel an order or get a refund, except when they were only asking how the policy works.' },
  { about: 'Agent',    text: 'The agent apologized but offered no fix or next step.' },
  { about: 'Agent',    text: 'The agent quoted a specific price or promised a delivery date.' },
  { about: 'Business Task', text: 'A renewal that failed to complete because a step errored or was declined.' },
  { about: 'Business Task', text: 'The agent looped on the same tool 3 or more times without making progress.' },
];

const ClassifierWizard = ({
  isOpen, onClose, onSave, existingClassifier = null,
  appId, environment, isDemo = false,
}) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null); // { name, status, matchCount, notMatchCount }
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [description, setDescription] = useState('');
  const [classifierKey, setClassifierKey] = useState('');
  const [matchType, setMatchType] = useState('semantic');
  const [generatedPatterns, setGeneratedPatterns] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [totalMatches, setTotalMatches] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [matches, setMatches] = useState(new Set());
  const [notMatches, setNotMatches] = useState(new Set());
  const [polarity, setPolarity] = useState('negative');
  const { selectedRange } = useTimeRange();
  const { getJwt } = useSessionJwt();

  useEffect(() => {
    if (name) setClassifierKey(toClassifierKey(name));
  }, [name]);

  useEffect(() => {
    if (isOpen) {
      setSaved(null);
      if (existingClassifier) {
        setName(existingClassifier.name || '');
        setPrompt(existingClassifier.prompt || existingClassifier.description || '');
        setDescription(existingClassifier.description || '');
        setClassifierKey(existingClassifier.classifier_key || '');
        // 'regex' is stored in DB but the wizard uses 'keyword' for the regex/keyword path
        const mt = existingClassifier.match_type || 'semantic';
        setMatchType(mt === 'regex' ? 'keyword' : mt);
        setGeneratedPatterns(existingClassifier.patterns || []);
        setPolarity(existingClassifier.polarity || 'negative');
        setSearchResults(null);
        setTotalMatches(0);
      } else {
        setName(''); setPrompt(''); setDescription(''); setClassifierKey('');
        setMatchType('semantic');
        setPolarity('negative');
        setGeneratedPatterns([]); setSearchResults(null); setTotalMatches(0);
      }
      setShowAdvanced(false);
      setSearchError(null);
      setMatches(new Set());
      setNotMatches(new Set());
    }
  }, [isOpen, existingClassifier]);

  const handleFindMatches = useCallback(async () => {
    if (!prompt.trim()) return;
    setSearching(true);
    setSearchError(null);

    try {
      let data;

      // Demo mode: use mock data
      if (isDemo) {
        // Simulate search delay
        await new Promise(resolve => setTimeout(resolve, 800));
        data = getMockClassifierTestResults(prompt.trim(), appId);

        if (data.matches.length === 0) {
          setSearchError('No matches found in demo data. Try: "Find customer queries about refund policy on Electronics, Washing Machines, Dishwashers and Refrigerators."');
        }
      } else {
        // Real mode: call backend API
        const jwt = getJwt();
        if (!jwt) {
          throw new Error('Not authenticated');
        }

        if (matchType === 'semantic') {
          // Semantic: use vector search
          const endpoint = `${apiUrl}${v2ServicePath(appId, '/traces/search')}?env=${encodeURIComponent(environment || '')}`;
          const searchBody = { query: prompt.trim(), limit: 20, range: selectedRange };
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${jwt}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(searchBody),
          });

          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || `HTTP ${response.status}`);
          }

          const json = await response.json();
          const rawResults = json.data || [];
          const matches = rawResults.map(r => ({
            trace_id: r.trace_id,
            input_text: r.input || r.first_user_input || '',
            output_text: r.output || r.last_assistant_output || '',
            matched_text: r.input || r.first_user_input || '',
            relevance_score: r.relevance_score,
          }));
          data = { matches, total_matches: matches.length, generated_patterns: {} };
        } else {
          // Keyword/regex: generate patterns and search canonical span text.
          const endpoint = `${apiUrl}${v2ServicePath(appId, '/classifiers/pattern-matches')}?env=${encodeURIComponent(environment || '')}`;
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${jwt}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              prompt: prompt.trim(),
              match_type: 'regex',
              search_fields: ['input', 'output'],
              case_sensitive: false,
              limit: 20,
            }),
          });

          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || `HTTP ${response.status}`);
          }

          const json = await response.json();
          data = json.data || {};
        }
      }

      // Backend/mock returns generated_patterns with { keywords: [...], regex: [...] }
      const gen = data.generated_patterns || {};
      const allPatterns = [...(gen.regex || []), ...(gen.keywords || [])];
      setGeneratedPatterns(allPatterns);

      setSearchResults(data.matches || []);
      setTotalMatches(data.total_matches || 0);
      setMatches(new Set());
      setNotMatches(new Set());
      if (!existingClassifier) setDescription(prompt.trim());
    } catch (err) {
      console.error('Failed to test patterns:', err);
      setSearchError(err.message);
      setSearchResults([]);
      setTotalMatches(0);
    } finally {
      setSearching(false);
    }
  }, [prompt, getJwt, appId, environment, existingClassifier, isDemo, matchType, selectedRange]);

  const handleLabel = useCallback((traceId, label) => {
    if (label === 'positive') {
      setMatches(prev => {
        const s = new Set(prev);
        s.has(traceId) ? s.delete(traceId) : s.add(traceId);
        return s;
      });
      setNotMatches(prev => { const s = new Set(prev); s.delete(traceId); return s; });
    } else {
      setNotMatches(prev => {
        const s = new Set(prev);
        s.has(traceId) ? s.delete(traceId) : s.add(traceId);
        return s;
      });
      setMatches(prev => { const s = new Set(prev); s.delete(traceId); return s; });
    }
  }, []);

  const handleSave = useCallback(async (draft) => {
    setSaving(true);
    setSaveError(null);
    try {
      const examples = searchResults
        ? searchResults
            .filter(r => matches.has(r.trace_id) || notMatches.has(r.trace_id))
            .map(r => ({
              trace_id: r.trace_id,
              label: notMatches.has(r.trace_id) ? 'negative' : 'positive',
            }))
        : [];

      const payload = {
        name,
        description: matchType === 'semantic' ? prompt.trim() : description,
        prompt: prompt.trim(),
        classifier_key: classifierKey,
        match_type: matchType === 'keyword' ? 'regex' : matchType,
        patterns: matchType === 'keyword' ? generatedPatterns : [],
        search_fields: ['input', 'output'],
        case_sensitive: false,
        status: draft ? 'draft' : 'active',
        service_id: appId,
        environment: environment,
        polarity: polarity,
        training_examples: examples,
      };

      await onSave?.(payload);

      const matchCount = examples.filter(e => e.label === 'positive').length;
      const notMatchCount = examples.filter(e => e.label === 'negative').length;
      setSaved({ name, status: draft ? 'draft' : 'active', matchCount, notMatchCount });
    } catch (err) {
      console.error('Failed to save classifier:', err);
      setSaveError(err?.message || 'Failed to save signal. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [name, description, prompt, classifierKey, matchType, generatedPatterns, searchResults, matches, notMatches, polarity, onSave, appId]);

  const canDeploy = name.trim().length > 0 && prompt.trim().length > 0;

  if (!isOpen) return null;

  const isKeyword = matchType === 'keyword';

  const SectionLabel = ({ children }) => (
    <h3
      className="text-xs uppercase mb-3"
      style={{ color: palette.text.muted, letterSpacing: '0.05em' }}
    >
      {children}
    </h3>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div
        className="relative w-[1200px] max-w-[95vw] max-h-[85vh] overflow-hidden flex flex-col rounded-xl"
        style={{ backgroundColor: 'var(--color-card-bg)', border: '1px solid var(--color-border-subtle)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid var(--color-border-subtle)' }}
        >
          <div>
            <h2 className="text-lg font-semibold" style={{ color: palette.text.primary }}>
              {existingClassifier ? 'Edit Signal' : 'New Signal'}
            </h2>
            <p className="text-sm" style={{ color: palette.text.muted }}>
              {existingClassifier ? 'Update signal configuration' : 'Describe a pattern to detect'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded transition-colors"
            style={{ color: palette.text.muted }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Confirmation state */}
        {saved && (
          <>
            <div className="flex-1 flex flex-col items-center justify-center p-8 gap-5 text-center">
              <CheckCircle2 size={40} style={{ color: palette.status.success }} />
              <div>
                <p className="text-base font-semibold mb-1" style={{ color: palette.text.primary }}>
                  {existingClassifier ? 'Signal updated' : 'Signal created'}
                </p>
                <p className="text-sm" style={{ color: palette.text.muted }}>
                  <span style={{ color: palette.text.primary }}>{saved.name}</span>
                  {saved.status === 'active' ? ' is now active and will evaluate new traces.' : ' has been saved as a draft.'}
                </p>
              </div>
              {(saved.matchCount > 0 || saved.notMatchCount > 0) && (
                <div
                  className="rounded-lg px-5 py-3 flex gap-6 text-sm"
                  style={{ backgroundColor: 'var(--color-table-row-expanded)', border: '1px solid var(--color-border-subtle)' }}
                >
                  <div className="text-center">
                    <p className="text-lg font-semibold" style={{ color: palette.text.primary }}>{saved.matchCount}</p>
                    <p style={{ color: palette.text.muted }}>labeled match{saved.matchCount !== 1 ? 'es' : ''}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold" style={{ color: palette.text.primary }}>{saved.notMatchCount}</p>
                    <p style={{ color: palette.text.muted }}>labeled non-match{saved.notMatchCount !== 1 ? 'es' : ''}</p>
                  </div>
                </div>
              )}
            </div>
            <div
              className="flex items-center justify-end px-6 py-4"
              style={{ borderTop: '1px solid var(--color-border-subtle)' }}
            >
              <button
                onClick={onClose}
                className="px-5 py-2 text-sm font-medium rounded"
                style={{ backgroundColor: palette.accents.primary.base, color: palette.text.inverse }}
              >
                Done
              </button>
            </div>
          </>
        )}

        {/* Content */}
        {!saved && <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Signal name */}
          <div>
            <SectionLabel>Signal Name</SectionLabel>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Cancellation Requests"
              className="w-full px-3 py-2 rounded text-sm focus:outline-none"
              style={{
                backgroundColor: 'var(--color-input-bg)',
                border: '1px solid var(--color-border-subtle)',
                color: palette.text.primary,
              }}
            />
          </div>

          {/* Match type selector - hidden in demo mode */}
          {!isDemo && (
            <div>
              <SectionLabel>Detection Type</SectionLabel>
              <div
                className="flex gap-1 p-1 rounded-lg"
                style={{ backgroundColor: 'var(--color-input-bg)' }}
              >
                {MATCH_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setMatchType(opt.value);
                      setSearchResults(null);
                      setSearchError(null);
                      setGeneratedPatterns([]);
                      setTotalMatches(0);
                    }}
                    className="flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                    style={{
                      backgroundColor: matchType === opt.value ? palette.accents.primary.base : 'transparent',
                      color: matchType === opt.value ? palette.text.inverse : palette.text.muted,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Detection description / criteria */}
          <div>
            <SectionLabel>What should this signal detect?</SectionLabel>
            <div className="relative">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={isDemo
                  ? "e.g., Find customer queries about refund policy on Electronics, Washing Machines, Dishwashers and Refrigerators."
                  : undefined}
                rows={8}
                className="w-full px-3 py-2 rounded text-sm resize-none focus:outline-none"
                style={{
                  backgroundColor: 'var(--color-input-bg)',
                  border: '1px solid var(--color-border-subtle)',
                  color: palette.text.primary,
                }}
              />
              {/* Empty-state guidance: hint (muted) + a blank line + dimmer, smaller examples */}
              {!isDemo && !prompt && (
                <div className="absolute inset-0 px-3 py-2 pointer-events-none" aria-hidden="true">
                  <div className="text-sm" style={{ color: palette.text.muted }}>
                    Describe one behavior: who (the User, Agent, or Business Task) did what, specifically (, except when ...)
                  </div>
                  <div className="mt-2 flex flex-col gap-0.5 text-xs" style={{ color: palette.text.muted }}>
                    {SIGNAL_EXAMPLES.map((ex, i) => (
                      <div key={i}>{ex.text}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Find Matches button */}
          {(
            <button
              type="button"
              onClick={handleFindMatches}
              disabled={!prompt.trim() || searching}
              className={`flex items-center gap-2 px-4 py-2 rounded text-sm w-full justify-center ${components.button.primary}`}
            >
              {searching ? (
                <><Loader2 size={16} className="animate-spin" /> {isDemo ? 'Searching...' : 'Generating patterns & searching...'}</>
              ) : (
                <><Sparkles size={16} /> Find Matches</>
              )}
            </button>
          )}

          {/* Error */}
          {searchError && (
            <div
              className="p-3 rounded-lg text-sm"
              style={{
                backgroundColor: withOpacity(palette.status.danger, 8),
                border: `1px solid ${withOpacity(palette.status.danger, 20)}`,
                color: palette.status.danger,
              }}
            >
              {searchError}
            </div>
          )}

          {/* Generated patterns (collapsible) - hidden in demo mode */}
          {!isDemo && isKeyword && generatedPatterns.length > 0 && (
            <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--color-border-subtle)' }}>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center justify-between w-full px-3 py-2 text-sm"
                style={{ backgroundColor: 'var(--color-table-row-expanded)', color: palette.text.muted }}
              >
                <span className="flex items-center gap-1.5">
                  <Search size={12} />
                  {generatedPatterns.length} pattern{generatedPatterns.length !== 1 ? 's' : ''} generated
                </span>
                {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showAdvanced && (
                <div className="px-3 py-2" style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
                  <div className="flex flex-wrap gap-1.5">
                    {generatedPatterns.map((p, i) => {
                      const isRegex = p.includes('.*');
                      return (
                        <code
                          key={i}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-mono"
                          style={{
                            backgroundColor: isRegex ? `${palette.accents.warm.base}18` : `${palette.accents.primary.base}18`,
                            color: isRegex ? palette.accents.warm.base : palette.accents.primary.base,
                          }}
                        >
                          {isRegex && <span className="mr-1 text-[10px] opacity-60">regex</span>}
                          {p}
                        </code>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Search results */}
          {searchResults && searchResults.length > 0 && (
            <div>
              <SectionLabel>
                {searchResults.length} matches shown{totalMatches > searchResults.length ? ` (${totalMatches} total)` : ''}
              </SectionLabel>
              <div className="max-h-56 overflow-y-auto rounded-lg" style={{ border: '1px solid var(--color-border-subtle)' }}>
                {searchResults.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 px-3 py-2.5"
                    style={{
                      backgroundColor: i % 2 === 0 ? 'var(--color-table-row-expanded)' : 'transparent',
                      borderTop: i > 0 ? '1px solid var(--color-border-subtle)' : 'none',
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono" style={{ color: palette.accents.primary.base }}>
                          {r.trace_id?.substring(0, 16) || '-'}
                        </span>
                        {r.relevance_score != null ? (
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                            style={{
                              backgroundColor: `${palette.accents.primary.base}20`,
                              color: palette.accents.primary.base,
                            }}
                          >
                            {Math.round(r.relevance_score * 100)}% match
                          </span>
                        ) : (
                          <>
                            <span
                              className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                              style={{
                                backgroundColor: r.matched_field === 'input' ? `${palette.accents.primary.base}20` : `${palette.accents.warm.base}20`,
                                color: r.matched_field === 'input' ? palette.accents.primary.base : palette.accents.warm.base,
                              }}
                            >
                              {r.matched_field}
                            </span>
                            {r.matched_pattern && (
                              <code
                                className="px-1.5 py-0.5 rounded text-[10px] font-mono"
                                style={{ backgroundColor: 'var(--color-table-row-hover)', color: palette.text.supporting }}
                              >
                                {r.matched_pattern}
                              </code>
                            )}
                            <span className="text-[10px]" style={{ color: palette.text.muted }}>{r.root_span_name}</span>
                          </>
                        )}
                      </div>
                      {r.relevance_score != null ? (
                        // Semantic result: show input + output
                        <div className="flex flex-col gap-1">
                          {r.input_text && (
                            <p className="text-xs font-mono break-words" style={{ color: palette.text.muted }}>
                              <span className="text-[10px] font-sans mr-1" style={{ color: palette.text.supporting }}>User:</span>
                              {(r.input_text || '').substring(0, 150)}
                            </p>
                          )}
                          {r.output_text && (
                            <p className="text-xs font-mono break-words" style={{ color: palette.text.muted }}>
                              <span className="text-[10px] font-sans mr-1" style={{ color: palette.accents.primary.base }}>Agent:</span>
                              {(r.output_text || '').substring(0, 150)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs font-mono break-words" style={{ color: palette.text.muted }}>
                          {r.highlight_start != null && r.highlight_end > r.highlight_start ? (
                            <>
                              {r.matched_text.substring(0, r.highlight_start)}
                              <span style={{
                                backgroundColor: `${palette.accents.warm.base}25`,
                                color: palette.accents.warm.base,
                                borderRadius: '2px',
                                padding: '0 1px',
                              }}>
                                {r.matched_text.substring(r.highlight_start, r.highlight_end)}
                              </span>
                              {r.matched_text.substring(r.highlight_end)}
                            </>
                          ) : (r.matched_text || '').substring(0, 200)}
                        </p>
                      )}
                    </div>
                    {/* Explicit label buttons */}
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => handleLabel(r.trace_id, 'positive')}
                        className="px-2 py-1 text-xs rounded transition-colors whitespace-nowrap"
                        style={{
                          backgroundColor: matches.has(r.trace_id) ? withOpacity(palette.status.success, 15) : 'transparent',
                          border: `1px solid ${matches.has(r.trace_id) ? palette.status.success : 'var(--color-border-subtle)'}`,
                          color: matches.has(r.trace_id) ? palette.status.success : palette.text.muted,
                        }}
                      >
                        {matches.has(r.trace_id) ? '✓ Match' : 'Match'}
                      </button>
                      <button
                        onClick={() => handleLabel(r.trace_id, 'negative')}
                        className="px-2 py-1 text-xs rounded transition-colors whitespace-nowrap"
                        style={{
                          backgroundColor: notMatches.has(r.trace_id) ? withOpacity(palette.status.danger, 15) : 'transparent',
                          border: `1px solid ${notMatches.has(r.trace_id) ? palette.status.danger : 'var(--color-border-subtle)'}`,
                          color: notMatches.has(r.trace_id) ? palette.status.danger : palette.text.muted,
                        }}
                      >
                        {notMatches.has(r.trace_id) ? '✓ Not a match' : 'Not a match'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {searchResults && searchResults.length > 0 && (
            <p className="text-xs" style={{ color: palette.text.muted }}>
              Label examples to help the signal learn. Only traces you explicitly label are saved.
            </p>
          )}

          {searchResults && searchResults.length === 0 && !searchError && (
            <div
              className="p-4 rounded-lg text-center text-sm"
              style={{
                backgroundColor: 'var(--color-table-row-expanded)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '0.5rem',
                color: palette.text.muted,
              }}
            >
              No matches found. Try describing the pattern differently.
            </div>
          )}
        </div>}

        {/* Footer */}
        {!saved && <div
          className="px-6 py-4"
          style={{ borderTop: '1px solid var(--color-border-subtle)' }}
        >
          {saveError && (
            <div
              className="mb-3 p-3 rounded-lg text-sm"
              style={{
                backgroundColor: withOpacity(palette.status.danger, 8),
                border: `1px solid ${withOpacity(palette.status.danger, 20)}`,
                color: palette.status.danger,
              }}
            >
              {saveError}
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => handleSave(true)}
              disabled={saving || !canDeploy}
              className="px-4 py-2 text-sm rounded disabled:opacity-50"
              style={{
                color: palette.text.primary,
                border: '1px solid var(--color-border-subtle)',
                backgroundColor: 'var(--color-surface-hover)',
              }}
            >
              Save as Draft
            </button>
            <button
              onClick={() => handleSave(false)}
              disabled={saving || !canDeploy}
              className={`flex items-center gap-2 px-4 py-2 rounded text-sm ${components.button.primary}`}
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {existingClassifier ? 'Save Changes' : 'Deploy Signal'}
            </button>
          </div>
        </div>}
      </div>
    </div>
  );
};

export default ClassifierWizard;
